/**
 * 상세 다이얼로그 왼쪽 캐러셀의 칸들 — 광고 소재 컷 · 게시물 이미지 여러 장 · 상품 대표 이미지 여러 장.
 *
 * 영상 칸은 재생할 수 있는 주소가 있을 때만 영상이고, 없으면 그 컷의 이미지로 남는다.
 * ⚠️ 새로 받은 영상 주소(`freshVideoUrl`)는 첫 영상 칸에만 쓴다 — 주소를 묻는 단위가 소재 하나라서다.
 */

import { pickPostImages } from 'src/utils/moments/view/post-media';

import { readSignedUrlExpiry } from './search-all-media-cards';

import type { MediaCard } from './search-all-media-cards';

export type MediaSlide = { type: 'image' | 'video'; image: string; video: string };

/** 서명 주소가 이만큼(ms) 안에 만료되면 재생하지 않는다 */
const EXPIRY_SKEW_MS = 60 * 1000;

/**
 * @param card 열린 카드
 * @param freshVideoUrl 새로 받은 영상 주소 — 없으면 빈 문자열
 * @param extraImages 더 붙일 이미지(상품 상세의 대표 이미지 등)
 * @param now 기준 시각 — 테스트가 고정한다
 */
export function toMediaSlides(card: MediaCard, freshVideoUrl: string, extraImages: string[] = [], now: number = Date.now()): MediaSlide[] {
  if (card.kind === 'ad' && card.ad) {
    let freshUsed = false;
    const slides = card.ad.media.map((m): MediaSlide => {
      if (m.type !== 'video') return { type: 'image', image: m.image, video: '' };
      const expiry = m.video ? readSignedUrlExpiry(m.video) : null;
      const alive = Boolean(m.video) && (expiry == null || expiry - now > EXPIRY_SKEW_MS);
      if (alive) return { type: 'video', image: m.image, video: m.video };
      if (freshVideoUrl && !freshUsed) {
        freshUsed = true;
        return { type: 'video', image: m.image, video: freshVideoUrl };
      }
      return { type: 'image', image: m.image, video: '' };
    }).filter((s) => s.image || s.video);
    return slides.length > 0 ? slides : fallback(card, freshVideoUrl);
  }

  if (card.kind === 'product' && card.product) {
    const images = unique([card.thumb, ...card.product.images, ...extraImages]);
    return images.map((image) => ({ type: 'image', image, video: '' }));
  }

  const images = pickPostImages(card.post ?? {});
  const video = card.videoSrc || freshVideoUrl;
  if (video) return [{ type: 'video', image: images[0] ?? card.thumb, video }, ...images.slice(1).map((image) => ({ type: 'image' as const, image, video: '' }))];
  const all = images.length > 0 ? images : unique([card.thumb]);
  return all.map((image) => ({ type: 'image', image, video: '' }));
}

function fallback(card: MediaCard, freshVideoUrl: string): MediaSlide[] {
  if (freshVideoUrl || card.videoSrc) return [{ type: 'video', image: card.thumb, video: card.videoSrc || freshVideoUrl }];
  return card.thumb ? [{ type: 'image', image: card.thumb, video: '' }] : [];
}

function unique(urls: string[]): string[] {
  return [...new Set(urls.filter((u) => typeof u === 'string' && u))];
}
